import * as React from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  Box,
  Breadcrumbs,
  Container,
  CssBaseline,
  Toolbar,
  Typography,
  CircularProgress,
  Alert,
  Card,
  Grid,
  Chip,
  Button,
  Tabs,
  Tab,
} from "@mui/material";
import PeopleIcon from "@mui/icons-material/People";
import Footer from "../components/Footer";
import Navigation from "../components/Navigation";
import SquarespacePageHeader from "../components/SquarespacePageHeader";
import CreateSquarespaceOrderDialog from "../components/admin/CreateSquarespaceOrderDialog";
import JsonDataModal from "../components/admin/JsonDataModal";
import WebsiteDetailModal from "../components/admin/WebsiteDetailModal";
import SubscriptionDetailModal from "../components/admin/SubscriptionDetailModal";
import OrdersList from "../components/admin/SquarespaceCustomers/OrdersList";
import SubscriptionsList from "../components/admin/SquarespaceCustomers/SubscriptionsList";
import PortalSubscriptionsList from "../components/admin/SquarespaceCustomers/PortalSubscriptionsList";
import WebsitesList from "../components/admin/SquarespaceCustomers/WebsitesList";
import { Link } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || "http://localhost:8787";

function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export default function SquarespaceCustomerDetail() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { getAccessTokenSilently } = useAuth0();
  const [customer, setCustomer] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  
  // Initialize tab from URL parameter, default to 0
  const getInitialTab = () => {
    const tabParam = searchParams.get("tab");
    if (tabParam) {
      const tabIndex = parseInt(tabParam, 10);
      if (!isNaN(tabIndex) && tabIndex >= 0 && tabIndex <= 4) { // Max tab index is 4 (Portal Subscriptions)
        return tabIndex;
      }
    }
    return 0;
  };
  
  const [tabValue, setTabValue] = React.useState(getInitialTab());
  const [orders, setOrders] = React.useState([]);
  const [ordersLoading, setOrdersLoading] = React.useState(false);
  const [ordersError, setOrdersError] = React.useState(null);
  const [subscriptions, setSubscriptions] = React.useState([]);
  const [subscriptionsLoading, setSubscriptionsLoading] = React.useState(false);
  const [subscriptionsError, setSubscriptionsError] = React.useState(null);
  const [websites, setWebsites] = React.useState([]);
  const [websitesLoading, setWebsitesLoading] = React.useState(false);
  const [websitesError, setWebsitesError] = React.useState(null);
  const [portalSubscriptions, setPortalSubscriptions] = React.useState([]);
  const [portalSubscriptionsTotal, setPortalSubscriptionsTotal] = React.useState(0);
  const [portalSubscriptionsLoading, setPortalSubscriptionsLoading] = React.useState(false);
  const [portalSubscriptionsError, setPortalSubscriptionsError] = React.useState(null);
  
  // Create order modal state
  const [createOrderDialogOpen, setCreateOrderDialogOpen] = React.useState(false);
  const [createOrderFormData, setCreateOrderFormData] = React.useState({
    customerId: "",
    pricingId: "",
    title: "",
    domain: "",
  });
  const [creatingOrder, setCreatingOrder] = React.useState(false);
  const [createOrderError, setCreateOrderError] = React.useState(null);
  const [createOrderApiResponse, setCreateOrderApiResponse] = React.useState(null);
  
  // JSON modal state for orders
  const [ordersJsonModalOpen, setOrdersJsonModalOpen] = React.useState(false);
  
  // JSON modal state for subscriptions
  const [subscriptionsJsonModalOpen, setSubscriptionsJsonModalOpen] = React.useState(false);
  
  // JSON modal state for websites
  const [websitesJsonModalOpen, setWebsitesJsonModalOpen] = React.useState(false);
  
  // JSON modal state for individual order
  const [selectedOrderJsonModalOpen, setSelectedOrderJsonModalOpen] = React.useState(false);
  const [selectedOrder, setSelectedOrder] = React.useState(null);
  // Modal state for full website details (fetched from SS API)
  const [websiteDetailModalOpen, setWebsiteDetailModalOpen] = React.useState(false);
  const [selectedWebsiteId, setSelectedWebsiteId] = React.useState(null);
  // Modal state for full subscription details (fetched from SS API)
  const [subscriptionDetailModalOpen, setSubscriptionDetailModalOpen] = React.useState(false);
  const [selectedSubscriptionId, setSelectedSubscriptionId] = React.useState(null);

  React.useEffect(() => {
    const fetchCustomer = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = await getAccessTokenSilently().catch(() => null);
        
        // Call the single customer endpoint
        const response = await fetch(`${API_BASE_URL}/admin/squarespace/customers/${id}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          if (response.status === 404) {
            throw new Error("Customer not found");
          }
          throw new Error(
            errorData.message || errorData.error || `Failed to fetch customer: ${response.status} ${response.statusText}`
          );
        }

        const responseData = await response.json();
        // API returns { data: {...} }
        const customerData = responseData.data || responseData;
        
        setCustomer(customerData);
      } catch (err) {
        console.error("Error fetching customer:", err);
        setError(err.message || "Failed to fetch customer");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchCustomer();
    }
  }, [id, getAccessTokenSilently]);

  // Fetch orders function (can be called manually for refresh)
  const fetchOrders = React.useCallback(async () => {
    if (!customer?.customerId) return;
    
    setOrdersLoading(true);
    setOrdersError(null);
    try {
      const token = await getAccessTokenSilently().catch(() => null);
      
      // Use the customer-specific orders endpoint
      const response = await fetch(`${API_BASE_URL}/admin/squarespace/customers/${customer.customerId}/orders`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || `Failed to fetch orders: ${response.status}`);
      }

      const responseData = await response.json();
      const customerOrders = responseData.data || [];
      
      setOrders(customerOrders);
    } catch (err) {
      console.error("Error fetching orders:", err);
      setOrdersError(err.message || "Failed to fetch orders");
    } finally {
      setOrdersLoading(false);
    }
  }, [customer, getAccessTokenSilently]);

  // Fetch orders when Orders tab is selected
  React.useEffect(() => {
    if (tabValue === 1 && customer?.customerId) {
      fetchOrders();
    }
  }, [tabValue, customer, fetchOrders]);

  // Fetch subscriptions function (can be called manually for refresh)
  const fetchSubscriptions = React.useCallback(async () => {
    if (!customer?.customerId) return;
    
    setSubscriptionsLoading(true);
    setSubscriptionsError(null);
    try {
      const token = await getAccessTokenSilently().catch(() => null);
      
      // Use the customer-specific subscriptions endpoint
      const response = await fetch(`${API_BASE_URL}/admin/squarespace/customers/${customer.customerId}/subscriptions`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || `Failed to fetch subscriptions: ${response.status}`);
      }

      const responseData = await response.json();
      const customerSubscriptions = responseData.data || [];
      
      setSubscriptions(customerSubscriptions);
    } catch (err) {
      console.error("Error fetching subscriptions:", err);
      setSubscriptionsError(err.message || "Failed to fetch subscriptions");
    } finally {
      setSubscriptionsLoading(false);
    }
  }, [customer, getAccessTokenSilently]);

  // Fetch subscriptions when Subscriptions tab is selected
  React.useEffect(() => {
    if (tabValue === 2 && customer?.customerId) {
      fetchSubscriptions();
    }
  }, [tabValue, customer, fetchSubscriptions]);

  // Fetch websites function (can be called manually for refresh)
  const fetchWebsites = React.useCallback(async () => {
    if (!customer?.customerId) return;
    
    setWebsitesLoading(true);
    setWebsitesError(null);
    try {
      const token = await getAccessTokenSilently().catch(() => null);
      
      // Use the customer-specific websites endpoint
      const response = await fetch(`${API_BASE_URL}/admin/squarespace/customers/${customer.customerId}/websites`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || `Failed to fetch websites: ${response.status}`);
      }

      const responseData = await response.json();
      const customerWebsites = responseData.data || [];
      
      setWebsites(customerWebsites);
    } catch (err) {
      console.error("Error fetching websites:", err);
      setWebsitesError(err.message || "Failed to fetch websites");
    } finally {
      setWebsitesLoading(false);
    }
  }, [customer, getAccessTokenSilently]);

  // Fetch websites when Websites tab is selected
  React.useEffect(() => {
    if (tabValue === 3 && customer?.customerId) {
      fetchWebsites();
    }
  }, [tabValue, customer, fetchWebsites]);

  const squarespaceCustomerIdForPortal =
    customer?.customerId || customer?.id || null;

  const fetchPortalSubscriptions = React.useCallback(async () => {
    const ssCustomerId = squarespaceCustomerIdForPortal;
    if (!ssCustomerId) return;

    setPortalSubscriptionsLoading(true);
    setPortalSubscriptionsError(null);
    try {
      const token = await getAccessTokenSilently().catch(() => null);
      const qs = new URLSearchParams({
        squarespace_customer_id: String(ssCustomerId),
        limit: "100",
        offset: "0",
      });
      const response = await fetch(
        `${API_BASE_URL}/admin/subscriptions?${qs.toString()}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || errorData.error || `Failed to fetch portal subscriptions: ${response.status}`
        );
      }

      const responseData = await response.json();
      const rows = responseData.data ?? [];
      const total = responseData.pagination?.total ?? rows.length;
      setPortalSubscriptions(Array.isArray(rows) ? rows : []);
      setPortalSubscriptionsTotal(typeof total === "number" ? total : rows.length);
    } catch (err) {
      console.error("Error fetching portal subscriptions:", err);
      setPortalSubscriptionsError(err.message || "Failed to fetch portal subscriptions");
      setPortalSubscriptions([]);
      setPortalSubscriptionsTotal(0);
    } finally {
      setPortalSubscriptionsLoading(false);
    }
  }, [squarespaceCustomerIdForPortal, getAccessTokenSilently]);

  React.useEffect(() => {
    if (tabValue === 4 && squarespaceCustomerIdForPortal) {
      fetchPortalSubscriptions();
    }
  }, [tabValue, squarespaceCustomerIdForPortal, fetchPortalSubscriptions]);

  // Sync tab value when URL parameter changes (e.g., browser back/forward)
  React.useEffect(() => {
    const tabParam = searchParams.get("tab");
    if (tabParam) {
      const tabIndex = parseInt(tabParam, 10);
      if (!isNaN(tabIndex) && tabIndex >= 0 && tabIndex <= 4) {
        setTabValue(tabIndex);
      }
    } else {
      // If no tab param, default to 0
      setTabValue(0);
    }
  }, [searchParams]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    // Update URL parameter to persist tab selection
    setSearchParams({ tab: newValue.toString() });
  };

  // Handle create order
  const handleCreateOrder = async () => {
    const effectiveCustomerId = customer?.customerId || createOrderFormData.customerId?.trim();
    if (!effectiveCustomerId) {
      setCreateOrderError("Customer ID is required");
      return;
    }

    if (!createOrderFormData.pricingId || !createOrderFormData.pricingId.trim()) {
      setCreateOrderError("Pricing ID is required");
      return;
    }
    if (!createOrderFormData.title || !createOrderFormData.title.trim()) {
      setCreateOrderError("Website title is required");
      return;
    }

    setCreatingOrder(true);
    setCreateOrderError(null);
    setCreateOrderApiResponse(null);

    try {
      const token = await getAccessTokenSilently().catch(() => null);
      
      const newOrderItemWebSite = {
        pricingId: createOrderFormData.pricingId.trim(),
        title: createOrderFormData.title.trim(),
      };
      if (createOrderFormData.domain && createOrderFormData.domain.trim()) {
        newOrderItemWebSite.domains = [createOrderFormData.domain.trim()];
      }
      
      const requestBody = {
        customerId: effectiveCustomerId.trim(),
        newOrderItemWebSite,
      };

      const response = await fetch(`${API_BASE_URL}/admin/squarespace/orders`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(requestBody),
      });

      const responseData = await response.json();

      if (!response.ok) {
        setCreateOrderApiResponse(responseData);
        setCreateOrderError(responseData.message || responseData.error || "Failed to create order");
        return;
      }

      setCreateOrderApiResponse({ success: true, data: responseData.data || responseData, message: "Order created successfully" });
      setCreateOrderError(null);
    } catch (err) {
      console.error("Error creating order:", err);
      const errPayload = { error: "Request failed", message: err.message || "Failed to create order" };
      setCreateOrderApiResponse(errPayload);
      setCreateOrderError(err.message || "Failed to create order");
    } finally {
      setCreatingOrder(false);
    }
  };

  const handleCreateOrderDialogClose = () => {
    const wasSuccess = createOrderApiResponse?.success || createOrderApiResponse?.data;
    setCreateOrderDialogOpen(false);
    setCreateOrderFormData({
      customerId: customer?.customerId || "",
      pricingId: "",
      title: "",
      domain: "",
    });
    setCreateOrderError(null);
    setCreateOrderApiResponse(null);
    if (wasSuccess) {
      fetchOrders();
      if (customer?.customerId) {
        setTimeout(() => fetchWebsites(), 2000);
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return "N/A";
    try {
      return `$${Number(amount).toFixed(2)}`;
    } catch {
      return amount;
    }
  };

  return (
    <>
      <Box sx={{ display: "flex" }}>
        <CssBaseline />
        <Navigation />
        <Box
          component="main"
          sx={{
            backgroundColor: (theme) => theme.palette.background.default,
            flexGrow: 1,
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            overflow: "auto",
          }}
        >
          <Toolbar />
          <Container maxWidth="lg" sx={{ mt: 2, mb: 4, flexGrow: 1 }}>
            <Box sx={{ mb: 2 }}>
              <Breadcrumbs aria-label="breadcrumb">
                <Typography
                  color="primary"
                  component={Link}
                  to="/dashboard"
                  sx={{
                    textDecoration: "none",
                    cursor: "pointer",
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Home
                </Typography>
                <Typography
                  color="primary"
                  component={Link}
                  to="/dashboard/squarespace"
                  sx={{
                    textDecoration: "none",
                    cursor: "pointer",
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Squarespace
                </Typography>
                <Typography
                  color="primary"
                  component={Link}
                  to="/dashboard/squarespace/customers"
                  sx={{
                    textDecoration: "none",
                    cursor: "pointer",
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Customers
                </Typography>
                <Typography color="text.primary" fontWeight={500}>
                  {customer?.firstName && customer?.lastName 
                    ? `${customer.firstName} ${customer.lastName}`.trim()
                    : customer?.email || customer?.customerId || customer?.id || id}
                </Typography>
              </Breadcrumbs>
            </Box>

            <SquarespacePageHeader
              title="Customer Details"
              description="View Squarespace customer information"
              icon={<PeopleIcon sx={{ color: "primary.main" }} />}
            />

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress />
              </Box>
            ) : customer ? (
              <>
              <Card variant="outlined">
                <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                  <Tabs 
                    value={tabValue} 
                    onChange={handleTabChange}
                    aria-label="customer detail tabs"
                    sx={{ minHeight: 48 }}
                  >
                    <Tab label="Customer Info" />
                    <Tab label="Orders" />
                    <Tab label="Subscriptions" />
                    <Tab label="Websites" />
                    <Tab label="Portal Subscriptions" />
                  </Tabs>
                </Box>

                <TabPanel value={tabValue} index={0}>
                  <Typography variant="h6" gutterBottom fontWeight="bold" sx={{ mb: 1.5 }}>
                    Customer Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                        Customer ID
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.25 }} fontFamily="monospace">
                        {customer.customerId || customer.id || "N/A"}
                      </Typography>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                        Status
                      </Typography>
                      <Box sx={{ mt: 0.25 }}>
                        <Chip
                          label={customer.status || "N/A"}
                          color={
                            customer.status === "ACTIVE" ? "success" :
                            customer.status === "PENDING" ? "warning" :
                            customer.status === "SUSPENDED" ? "error" :
                            customer.status === "TERMINATED" ? "default" : "default"
                          }
                          size="small"
                        />
                      </Box>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                        Account Verified
                      </Typography>
                      <Box sx={{ mt: 0.25 }}>
                        <Chip
                          label={customer.accountVerified ? "Yes" : "No"}
                          color={customer.accountVerified ? "success" : "warning"}
                          size="small"
                        />
                      </Box>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                        First Name
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.25 }}>
                        {customer.firstName || "N/A"}
                      </Typography>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                        Last Name
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.25 }}>
                        {customer.lastName || "N/A"}
                      </Typography>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                        Email
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.25 }}>
                        {customer.email || "N/A"}
                      </Typography>
                    </Grid>

                    {customer.verificationUrl && (
                      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                          Verification URL
                        </Typography>
                        <Box sx={{ mt: 0.25 }}>
                          <Button
                            variant="outlined"
                            size="small"
                            href={customer.verificationUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ textTransform: "none" }}
                          >
                            Open Verification Link
                          </Button>
                        </Box>
                      </Grid>
                    )}

                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                        Language
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.25 }}>
                        {customer.language || customer.locale || "N/A"}
                      </Typography>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                        Country
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.25 }}>
                        {customer.country || "N/A"}
                      </Typography>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                        Region
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.25 }}>
                        {customer.region || "N/A"}
                      </Typography>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                        Timezone
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.25 }}>
                        {customer.timezone || "N/A"}
                      </Typography>
                    </Grid>

                    {customer.socialAccount && (
                      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                          Login Method
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.25 }}>
                          {customer.socialAccount}
                        </Typography>
                      </Grid>
                    )}

                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                        Created On
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.25 }}>
                        {formatDate(customer.createdOn || customer.created_at || customer.createdAt)}
                      </Typography>
                    </Grid>

                    {Object.entries(customer).map(([key, value]) => {
                      // Skip already displayed fields
                      const displayedFields = [
                        'customerId', 'id', 'status', 'accountVerified', 'firstName', 'lastName', 
                        'email', 'verificationUrl', 'language', 'locale', 'country', 'region', 
                        'timezone', 'socialAccount', 'createdOn', 'created_at', 'createdAt',
                        'updatedOn', 'updated_at', 'updatedAt', 'acceptedAt'
                      ];
                      if (displayedFields.includes(key)) return null;
                      if (value === null || value === undefined) return null;
                      
                      return (
                        <Grid key={key} size={{ xs: 12, sm: 6, md: 4 }}>
                          <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                            {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 0.25 }}>
                            {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
                          </Typography>
                        </Grid>
                      );
                    })}

                    <Grid size={{ xs: 12 }}>
                      <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: "divider" }}>
                        <Typography variant="h6" gutterBottom fontWeight="bold">
                          Full Customer Data
                        </Typography>
                        <Box
                          component="pre"
                          sx={{
                            p: 2,
                            bgcolor: "grey.50",
                            borderRadius: 1,
                            overflow: "auto",
                            fontSize: "0.875rem",
                            fontFamily: "monospace",
                          }}
                        >
                          {JSON.stringify(customer, null, 2)}
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </TabPanel>

                <TabPanel value={tabValue} index={1}>
                  <OrdersList
                    orders={orders}
                    loading={ordersLoading}
                    error={ordersError}
                    onRefresh={fetchOrders}
                    onViewAllJson={() => setOrdersJsonModalOpen(true)}
                    onAddOrder={() => {
                      setCreateOrderFormData({
                        customerId: customer?.customerId || "",
                        pricingId: "",
                        title: "",
                        domain: "",
                      });
                      setCreateOrderDialogOpen(true);
                      setCreateOrderError(null);
                      setCreateOrderApiResponse(null);
                    }}
                    onViewOrderJson={(order) => {
                      setSelectedOrder(order);
                      setSelectedOrderJsonModalOpen(true);
                    }}
                    formatDate={formatDate}
                  />
                </TabPanel>

                <TabPanel value={tabValue} index={2}>
                  <SubscriptionsList
                    subscriptions={subscriptions}
                    loading={subscriptionsLoading}
                    error={subscriptionsError}
                    onRefresh={fetchSubscriptions}
                    onViewAllJson={() => setSubscriptionsJsonModalOpen(true)}
                    onViewSubscriptionDetails={(sub) => {
                      setSelectedSubscriptionId(sub?.id || sub?.subscriptionId);
                      setSubscriptionDetailModalOpen(true);
                    }}
                    formatDate={formatDate}
                    formatCurrency={formatCurrency}
                  />
                </TabPanel>

                <TabPanel value={tabValue} index={3}>
                  <WebsitesList
                    websites={websites}
                    loading={websitesLoading}
                    error={websitesError}
                    onRefresh={fetchWebsites}
                    onViewAllJson={() => setWebsitesJsonModalOpen(true)}
                    onViewWebsiteDetails={(website) => {
                      setSelectedWebsiteId(website?.id || website?.websiteId);
                      setWebsiteDetailModalOpen(true);
                    }}
                  />
                </TabPanel>

                <TabPanel value={tabValue} index={4}>
                  <PortalSubscriptionsList
                    subscriptions={portalSubscriptions}
                    loading={portalSubscriptionsLoading}
                    error={portalSubscriptionsError}
                    apiTotal={portalSubscriptionsTotal}
                    onRefresh={fetchPortalSubscriptions}
                    squarespaceCustomerId={
                      squarespaceCustomerIdForPortal
                        ? String(squarespaceCustomerIdForPortal)
                        : undefined
                    }
                  />
                </TabPanel>

              </Card>

              <Box sx={{ mt: 2, p: 2, borderRadius: 1, backgroundColor: "action.hover" }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Link Squarespace Customer ID in BryteBridge
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  To link this Squarespace customer to BryteBridge, add <code style={{ fontFamily: "monospace" }}>squarespace_customer_id</code> in two places:
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    p: 1.5,
                    borderRadius: 1,
                    backgroundColor: (theme) => theme.palette.mode === "dark" ? theme.palette.grey[800] : theme.palette.grey[100],
                    fontSize: "0.875rem",
                    fontFamily: "monospace",
                    overflow: "auto",
                  }}
                >
                  {JSON.stringify(
                    { squarespace_customer_id: customer.customerId || customer.id || "—" },
                    null,
                    2
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                  <strong>1. Subscription</strong> — Go to Subscription detail → Edit meta_data → add <code style={{ fontFamily: "monospace" }}>squarespace_customer_id</code> with the value above.
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                  <strong>2. Organization → Contacts</strong> — Find the contact with email <strong>{customer.email || "—"}</strong> → Edit meta_data → add <code style={{ fontFamily: "monospace" }}>squarespace_customer_id</code> with the value above.
                </Typography>
              </Box>
              </>
            ) : (
              <Alert severity="warning">
                Customer not found
              </Alert>
            )}
          </Container>
          <Footer />
        </Box>
      </Box>
      
      <CreateSquarespaceOrderDialog
        open={createOrderDialogOpen}
        onClose={handleCreateOrderDialogClose}
        formData={createOrderFormData}
        onFormDataChange={setCreateOrderFormData}
        onCreate={handleCreateOrder}
        creating={creatingOrder}
        error={createOrderError}
        customerId={customer?.customerId}
        apiResponse={createOrderApiResponse}
      />
      
      <JsonDataModal
        open={ordersJsonModalOpen}
        onClose={() => setOrdersJsonModalOpen(false)}
        data={{
          orders: orders,
          customerId: customer?.customerId,
          customerEmail: customer?.email,
        }}
        title="Orders JSON - Customer Orders"
      />
      
      <JsonDataModal
        open={subscriptionsJsonModalOpen}
        onClose={() => setSubscriptionsJsonModalOpen(false)}
        data={{
          subscriptions: subscriptions,
          customerId: customer?.customerId,
          customerEmail: customer?.email,
        }}
        title="Subscriptions JSON - Customer Subscriptions"
      />
      
      <JsonDataModal
        open={websitesJsonModalOpen}
        onClose={() => setWebsitesJsonModalOpen(false)}
        data={{
          websites: websites,
          customerId: customer?.customerId,
          customerEmail: customer?.email,
        }}
        title="Websites JSON - Customer Websites"
      />
      
      <WebsiteDetailModal
        open={websiteDetailModalOpen}
        onClose={() => {
          setWebsiteDetailModalOpen(false);
          setSelectedWebsiteId(null);
        }}
        websiteId={selectedWebsiteId}
        websiteDisplayId={selectedWebsiteId}
      />
      
      <SubscriptionDetailModal
        open={subscriptionDetailModalOpen}
        onClose={() => {
          setSubscriptionDetailModalOpen(false);
          setSelectedSubscriptionId(null);
        }}
        subscriptionId={selectedSubscriptionId}
        subscriptionDisplayId={selectedSubscriptionId}
      />
      
      <JsonDataModal
        open={selectedOrderJsonModalOpen}
        onClose={() => {
          setSelectedOrderJsonModalOpen(false);
          setSelectedOrder(null);
        }}
        data={selectedOrder}
        title={`Order JSON - ${selectedOrder?.id || selectedOrder?.orderId || "Order"}`}
      />
    </>
  );
}
